import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { useAuth } from "@/hooks/use-auth";
import { queryKeys } from "@/lib/query-keys";

export interface VocabularyNote {
  id: string;
  item_id: string | null;
  practice_session_id: string | null;
  content: string;
  updated_at: string;
  /** Joined from the `vocabulary` table (per-session generated vocab). */
  word: string | null;
  definition: string | null;
  ipa: string | null;
  type: string | null;
}

/** Upper bound on vocabulary notes fetched — avoids an unbounded scan. */
const NOTES_FETCH_CAP = 200;

/**
 * Upper bound on `vocabulary` ids collected for a word-scoped lookup, so the
 * `item_id.in.(…)` filter stays a sane URL. One word across every session a
 * learner has practised is far below this in practice.
 */
const WORD_MATCH_CAP = 100;

export interface VocabularyNotesQueryOptions {
  /** Cap the list (the sidebar shows five titles, not the whole archive). */
  limit?: number;
  /** Skip the `content` column; the note body is only needed by the notes pages. */
  includeContent?: boolean;
  /** Skip the query — the sidebar's list is collapsed on most page loads. */
  enabled?: boolean;
  /**
   * Scope the query to one word instead of the user's whole notes archive.
   * Surfaces that show notes *for a word* (dictionary definition tab, the word
   * detail sheet, the flashcard back) used to pull every note and filter in
   * memory: one word cost up to 200 note bodies, almost all discarded.
   */
  word?: string;
}

type NoteRow = {
  id: string;
  item_id: string | null;
  content?: string;
  updated_at: string;
  practice_session_id: string | null;
  title: string | null;
};

type VocabRow = {
  id: string;
  word: string;
  definition: string;
  ipa: string | null;
  type: string | null;
};

const VOCAB_COLUMNS = "id, word, definition, ipa, type";

/**
 * Wrap a value for a PostgREST filter. Inside `or=(…)` a double-quoted value is
 * how you pass spaces, commas and parentheses — which a word can legitimately
 * contain ("give up") and which would otherwise split the filter list.
 */
function quotedFilterValue(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function mapNotesToVocabulary(notes: NoteRow[], vocabById: Map<string, VocabRow>): VocabularyNote[] {
  return notes.map((n) => {
    const v = n.item_id ? vocabById.get(n.item_id) : undefined;
    return {
      id: n.id,
      item_id: n.item_id,
      practice_session_id: n.practice_session_id,
      content: n.content ?? "",
      updated_at: n.updated_at,
      word: v?.word ?? n.title ?? null,
      definition: v?.definition ?? null,
      ipa: v?.ipa ?? null,
      type: v?.type ?? null,
    };
  });
}

/**
 * The user's whole vocabulary-notes archive (newest first, capped).
 * Notes created during practice carry `item_id` → a `vocabulary` row (no FK, so
 * the word/definition are fetched in a second query and merged client-side).
 * Notes created standalone have no `item_id`; their word is stored in `title`.
 */
async function fetchVocabularyNoteArchive(
  userId: string,
  limit: number,
  includeContent: boolean
): Promise<VocabularyNote[]> {
  // Conditional select list — see the note in use-personal-notes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: noteRows, error } = await (db as any).from("notes")
    .select(
      includeContent
        ? "id, item_id, content, updated_at, practice_session_id, title"
        : "id, item_id, updated_at, practice_session_id, title"
    )
    .eq("user_id", userId)
    .eq("item_type", "vocabulary")
    .order("updated_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  const notes = (noteRows || []) as NoteRow[];

  const itemIds = [...new Set(notes.map((n) => n.item_id).filter(Boolean))] as string[];
  const vocabById = new Map<string, VocabRow>();

  if (itemIds.length > 0) {
    const { data: vocabRows } = await db.from("vocabulary").select(VOCAB_COLUMNS).in("id", itemIds);
    for (const v of (vocabRows || []) as VocabRow[]) vocabById.set(v.id, v);
  }

  return mapNotesToVocabulary(notes, vocabById);
}

/**
 * Notes for one word: two bounded requests instead of the archive.
 *
 * 1. Which of *this user's* vocabulary items carry that word (the join is scoped
 *    by session owner, so the id list stays small and cannot leak other users'
 *    rows — RLS would hide them anyway).
 * 2. Their notes, plus standalone notes that store the word in `title`.
 *
 * The returned vocabulary rows double as the enrichment map, so the usual second
 * "fetch vocabulary for every item_id" query is not needed.
 */
async function fetchVocabularyNotesForWord(
  userId: string,
  word: string,
  limit: number,
  includeContent: boolean
): Promise<VocabularyNote[]> {
  const match = quotedFilterValue(word);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: vocabData } = await (db as any).from("vocabulary")
    .select(`${VOCAB_COLUMNS}, practice_sessions!inner(user_id)`)
    .or(`word.ilike.${match}`)
    .eq("practice_sessions.user_id", userId)
    .limit(WORD_MATCH_CAP);

  const vocabRows = ((vocabData || []) as Array<VocabRow & { practice_sessions?: unknown }>).map((row) => {
    const { practice_sessions: _join, ...rest } = row;
    return rest as VocabRow;
  });
  const vocabById = new Map<string, VocabRow>();
  for (const v of vocabRows) vocabById.set(v.id, v);

  const itemIds = vocabRows.map((v) => v.id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query = (db as any).from("notes")
    .select(
      includeContent
        ? "id, item_id, content, updated_at, practice_session_id, title"
        : "id, item_id, updated_at, practice_session_id, title"
    )
    .eq("user_id", userId)
    .eq("item_type", "vocabulary")
    .order("updated_at", { ascending: false })
    .limit(limit);

  query = itemIds.length
    ? query.or(`title.ilike.${match},item_id.in.(${itemIds.join(",")})`)
    : query.or(`title.ilike.${match}`);

  const { data: noteRows, error } = await query;
  if (error) throw error;
  const notes = (noteRows || []) as NoteRow[];

  // A title-matched note may point at a vocabulary row whose word differs from
  // the searched one (e.g. a plural or a phrase). Those are normally none; when
  // there are any, resolve just those ids.
  const unresolved = [
    ...new Set(notes.map((n) => n.item_id).filter((id): id is string => !!id && !vocabById.has(id))),
  ];
  if (unresolved.length > 0) {
    const { data: extra } = await db.from("vocabulary").select(VOCAB_COLUMNS).in("id", unresolved);
    for (const v of (extra || []) as VocabRow[]) vocabById.set(v.id, v);
  }

  return mapNotesToVocabulary(notes, vocabById);
}

/**
 * Lists the user's vocabulary notes — `notes` rows with item_type='vocabulary'.
 * Pass `word` to scope to a single word (see `fetchVocabularyNotesForWord`);
 * omit it for the archive (`fetchVocabularyNoteArchive`).
 */
export const useVocabularyNotes = (options: VocabularyNotesQueryOptions = {}) => {
  const { limit = NOTES_FETCH_CAP, includeContent = true, enabled = true, word } = options;
  const { userId, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const baseKey = queryKeys.notes.vocabulary(userId);
  const normalizedWord = word?.trim().toLowerCase() || null;
  const listKey = [
    ...baseKey,
    normalizedWord ? "word" : "archive",
    normalizedWord,
    includeContent ? "full" : "meta",
    limit,
  ] as const;

  const query = useQuery({
    queryKey: listKey,
    enabled: enabled && !!userId && !!isAuthenticated,
    queryFn: async (): Promise<VocabularyNote[]> =>
      normalizedWord
        ? fetchVocabularyNotesForWord(userId!, normalizedWord, limit, includeContent)
        : fetchVocabularyNoteArchive(userId!, limit, includeContent),
    // A word's notes are stable for a practice session; re-revealing the same
    // card should not re-issue both requests. Mutations still invalidate.
    staleTime: normalizedWord ? 1000 * 60 : 0,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: baseKey });

  // Create a standalone vocabulary note for a word (e.g. from the vocabulary
  // preview). The word is stored in `title` since there's no practice session
  // / vocabulary row to point `item_id` at.
  const createNote = useMutation({
    mutationFn: async (input: { word: string; content: string }) => {
      const { error } = await db.from("notes").insert({
        user_id: userId!,
        item_type: "vocabulary",
        title: input.word.trim() || null,
        content: input.content ?? "",
      });

      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateNote = useMutation({
    mutationFn: async (input: { id: string; content: string }) => {
      const { error } = await db.from("notes")
        .update({ content: input.content ?? "" })
        .eq("id", input.id)
        .eq("user_id", userId!);

      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from("notes")
        .delete()
        .eq("id", id)
        .eq("user_id", userId!);

      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    notes: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    isAuthenticated: !!isAuthenticated,
    createNote,
    updateNote,
    deleteNote,
  };
};
